/**
 * Typed profile section transformer.
 *
 * The Orval-generated `ProfileDetailSchema` exposes `education`, `certifications`,
 * `languages`, and `experiences` items as `{ [key: string]: unknown }`. These four
 * named pure readers convert those loose entries into the strict
 * `Candidate*` shapes consumed by the profile UI.
 *
 * Each reader:
 *   - accepts `ProfileDetailSchema | null | undefined`
 *   - returns `[]` when the profile or section is missing
 *   - filters non-object entries (so a stray `null`/string/number in the array is dropped)
 *   - defaults every field, never throws on malformed input
 *
 * This file is the single seam where unknown-shape generated models meet the
 * strict consumer types and replaces the `(profile.X ?? []) as unknown as Candidate*`
 * casts across the five profile entry points. It is intentionally separate from
 * `transform-profile.ts`, which targets the search-results card consumer.
 */

import type { CandidateProfile } from "@/lib/api/types";
import type {
  CandidateCertification,
  CandidateEducation,
  CandidateExperience,
  CandidateLanguage,
} from "@/lib/api/types";

// ── Safe coercion helpers ────────────────────────────────────────────

type Loose = { [key: string]: unknown };

function isLooseObject(value: unknown): value is Loose {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readNullableString(obj: Loose, key: string): string | null {
  const value = obj[key];
  if (typeof value === "string") return value;
  return null;
}

function readNullableNumber(obj: Loose, key: string): number | null {
  const value = obj[key];
  if (typeof value === "number" && Number.isFinite(value)) return value;
  return null;
}

function readBoolean(obj: Loose, key: string): boolean {
  return obj[key] === true;
}

function readStringArray(obj: Loose, key: string): string[] {
  const value = obj[key];
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string");
}

function readRequiredString(obj: Loose, key: string): string {
  const value = obj[key];
  if (typeof value === "string") return value;
  return "";
}

// ── Public readers ───────────────────────────────────────────────────

/**
 * Convert the loose `profile.education` array into strictly typed
 * `CandidateEducation[]`. Returns `[]` for missing / null / undefined input
 * and drops any non-object entries.
 */
export function readEducation(
  profile: CandidateProfile | null | undefined,
): CandidateEducation[] {
  const list = profile?.education;
  if (!Array.isArray(list)) return [];
  return list
    .filter(Boolean)
    .filter(isLooseObject)
    .map(
      (raw): CandidateEducation => ({
        school_name: readNullableString(raw, "school_name"),
        school_domain: readNullableString(raw, "school_domain"),
        school_type: readNullableString(raw, "school_type"),
        school_location_country: readNullableString(raw, "school_location_country"),
        school_location_name: readNullableString(raw, "school_location_name"),
        degrees: readStringArray(raw, "degrees"),
        majors: readStringArray(raw, "majors"),
        minors: readStringArray(raw, "minors"),
        gpa: readNullableNumber(raw, "gpa"),
        start_date: readNullableString(raw, "start_date"),
        end_date: readNullableString(raw, "end_date"),
      }),
    );
}

/**
 * Convert the loose `profile.certifications` array into strictly typed
 * `CandidateCertification[]`. Returns `[]` for missing / null / undefined input
 * and drops any non-object entries.
 */
export function readCertifications(
  profile: CandidateProfile | null | undefined,
): CandidateCertification[] {
  const list = profile?.certifications;
  if (!Array.isArray(list)) return [];
  return list
    .filter(Boolean)
    .filter(isLooseObject)
    .map(
      (raw): CandidateCertification => ({
        name: readRequiredString(raw, "name"),
        organization: readNullableString(raw, "organization"),
        certificate_number: readNullableString(raw, "certificate_number"),
        start_date: readNullableString(raw, "start_date"),
        end_date: readNullableString(raw, "end_date"),
      }),
    );
}

/**
 * Convert the loose `profile.languages` array into strictly typed
 * `CandidateLanguage[]`. Returns `[]` for missing / null / undefined input
 * and drops any non-object entries.
 */
export function readLanguages(
  profile: CandidateProfile | null | undefined,
): CandidateLanguage[] {
  const list = profile?.languages;
  if (!Array.isArray(list)) return [];
  return list
    .filter(Boolean)
    .filter(isLooseObject)
    .map(
      (raw): CandidateLanguage => ({
        name: readRequiredString(raw, "name"),
        proficiency: readNullableString(raw, "proficiency"),
      }),
    );
}

/**
 * Convert the loose `profile.experiences` array into strictly typed
 * `CandidateExperience[]`. Returns `[]` for missing / null / undefined input
 * and drops any non-object entries.
 */
export function readExperiences(
  profile: CandidateProfile | null | undefined,
): CandidateExperience[] {
  const list = profile?.experiences;
  if (!Array.isArray(list)) return [];
  return list
    .filter(Boolean)
    .filter(isLooseObject)
    .map(
      (raw): CandidateExperience => ({
        company_id: readNullableString(raw, "company_id"),
        title_name: readNullableString(raw, "title_name"),
        title_role: readNullableString(raw, "title_role"),
        title_class: readNullableString(raw, "title_class"),
        title_sub_role: readNullableString(raw, "title_sub_role"),
        title_level: readNullableString(raw, "title_level"),
        company_name: readNullableString(raw, "company_name"),
        company_domain: readNullableString(raw, "company_domain"),
        company_industry: readNullableString(raw, "company_industry"),
        company_size: readNullableString(raw, "company_size"),
        company_founded: readNullableNumber(raw, "company_founded"),
        company_location_name: readNullableString(raw, "company_location_name"),
        start_date: readNullableString(raw, "start_date"),
        end_date: readNullableString(raw, "end_date"),
        is_primary: readBoolean(raw, "is_primary"),
        summary: readNullableString(raw, "summary"),
      }),
    );
}
